import PageHeader from '../components/PageHeader.jsx'
import { Link } from 'react-router-dom'
import { IconArrow } from '../components/icons.jsx'
import { AI_TOOLS, gptUrl, accessMeta, hasOwnerOnly, isLive, STUDIO_RELATIONSHIP } from '../lib/aiTools.js'
import styles from './AiTools.module.css'

/**
 * AI Tools — the launcher for SR's Custom GPTs.
 *
 * Brian: "Load that gpt into the portal."
 *
 * WHY IT IS A LAUNCHER. A Custom GPT lives on ChatGPT and has no embed API —
 * there is no iframe and no key that would let the hub run one. Same discipline
 * as Pricing, Presentations and Team Meeting Rooms: the hub is the front door,
 * the other application stays the source of truth. See lib/aiTools.js.
 *
 * WHY THE ACCESS NOTICE IS THE FIRST THING ON THE PAGE. Every GPT here is
 * shared "Only me" in ChatGPT today, so a teammate clicking through is refused
 * by ChatGPT. Shipping a wall of links without saying that would cost every
 * person on the team the same wasted minute, discovered privately. The gap is
 * named once, at the top, with what it would actually take to close it.
 */
export default function AiTools() {
  const ownerOnly = hasOwnerOnly()

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="SR internal"
        title="AI Tools"
        lead="The Custom GPTs we've built for SR work — each one tuned for a job we do over and over, so nobody starts from a blank chat. They run on ChatGPT and open in a new tab."
      />

      {/* THE GAP, NAMED. Not a footnote. */}
      {ownerOnly && (
        <div className={styles.notice}>
          <span className={styles.noticeTag}>Access</span>
          <div>
            <p className={styles.noticeLead}>
              These are shared <strong>“Only me”</strong> on Brian’s ChatGPT
              account. If you open one and ChatGPT says you don’t have access,
              nothing is broken here — that refusal is ChatGPT’s.
            </p>
            <p className={styles.noticeSub}>
              ChatGPT no longer allows sharing a GPT publicly, so opening these
              to the team needs an SR ChatGPT <strong>Business workspace</strong>{' '}
              — the GPTs move into it and everyone signs in with their own SR
              account. Until then the instructions behind each one can be handed
              out and pasted into any chat; ask Brian.
            </p>
            {/* THE CORRECTION. The vendor handoff said building a Custom GPT
                needs a Team/Business plan. It does not, and stating that
                plainly here is what stops the wrong version being repeated the
                next time somebody reads this page. */}
            <p className={styles.noticeSub}>
              <strong>Building</strong> one is not the constraint — a personal
              ChatGPT Plus account creates Custom GPTs fine, and every GPT here
              was built on one. Only <strong>sharing</strong> needs the
              workspace.
            </p>
          </div>
        </div>
      )}

      <div className={styles.grid}>
        {AI_TOOLS.map((tool) => {
          const url = gptUrl(tool)
          const access = accessMeta(tool)
          return (
            <article key={tool.slug} className={styles.card}>
              <div className={styles.cardTop}>
                <img
                  className={styles.seal}
                  src="/brand/sr-monogram.png"
                  alt=""
                  width="30"
                  height="30"
                  aria-hidden="true"
                />
                <span
                  className={`${styles.access} ${styles[`access_${access.tone}`]}`}
                  title={access.hint}
                >
                  {access.label}
                </span>
              </div>

              <h2 className={styles.name}>{tool.name}</h2>
              <p className={styles.blurb}>{tool.blurb}</p>

              {/* The field that decides whether anybody ever opens it. */}
              <p className={styles.useIt}>
                <span className={styles.useItK}>Reach for it when</span>
                {tool.useIt}
              </p>

              {tool.keeps?.length > 0 && (
                <ul className={styles.keeps}>
                  {tool.keeps.map((k) => (
                    <li key={k}>{k}</li>
                  ))}
                </ul>
              )}

              {tool.modes?.length > 0 && (
                <div className={styles.modes}>
                  {tool.modes.map((m) => (
                    <span key={m} className={styles.mode}>
                      {m}
                    </span>
                  ))}
                </div>
              )}

              <div className={styles.actions}>
                {url ? (
                  <a
                    className={`btn btn-primary ${styles.cta}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in ChatGPT
                    <IconArrow width={16} height={16} />
                  </a>
                ) : (
                  /* NEVER A DEAD BUTTON — and a staged tool says what it is
                     waiting on rather than the generic message, because
                     "not created yet" and "this entry is malformed" are two
                     different problems with two different fixes. */
                  <span className={styles.noLink}>
                    {!isLive(tool) && tool.awaiting ? tool.awaiting : 'No link on this entry yet'}
                  </span>
                )}

                {tool.pairsWith && (
                  <Link className={`btn ${styles.ctaGhost}`} to={tool.pairsWith.to}>
                    {tool.pairsWith.label}
                  </Link>
                )}
              </div>

              <span className={styles.owner}>Built by {tool.owner}</span>
            </article>
          )
        })}
      </div>

      {/* THE RELATIONSHIP, IN THE SAME WORDS THE STUDIO USES. Rendered from
          the shared constant so the two pages cannot describe one capability
          two ways — see lib/aiTools.js#STUDIO_RELATIONSHIP. */}
      <p className={styles.foot}>{STUDIO_RELATIONSHIP}</p>

      <p className={styles.foot}>
        These are doors, not copies. A Custom GPT can’t be embedded — it runs on
        ChatGPT with your own sign-in, and ChatGPT keeps the conversation. The
        hub deliberately holds none of it, for the same reason it links the
        pricing engine rather than re-implementing it.
      </p>
    </div>
  )
}
